import { type Client, createClient } from 'graphql-ws';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';

import {
	WS_CLIENT_CAPPED_RETRIES,
	WS_CLIENT_CONNECTION_ACK_WAIT,
	WS_CLIENT_KEEP_ALIVE,
	WS_CLIENT_LAZY_CLOSE,
	WS_CLIENT_LIVENESS_KEEP_ALIVE,
	WS_CLIENT_MAX_RETRY_WAIT,
	WS_CLIENT_PONG_WAIT,
	WS_CLIENT_RETRIES,
} from '../../constants';
import { getAggSubscriptionEndpoint, isSSR } from '../index';
import {
	acquireClient,
	createClientState,
	setEndpointRecoveryEnabled,
	type ClientLease,
	type ClientState,
} from './client-pool';
import {
	addRecoveryListeners,
	createRateLimitedRetryWait,
	createRetryWait,
	shouldRetryTransportFailure,
} from './recovery';
import { createSocketListeners, type WsConnectionEvent } from './socketListeners';

export type { WsConnectionEvent } from './socketListeners';

/** Product-supplied connection reporting. Transport options stay in this package. */
export type WsClientOptions = {
	/**
	 * Getter so `undefined` reports nothing. A no-op reporter would still pay listener cost
	 * when listeners are attached for reporting alone; the reliability gate may attach them
	 * for the pong deadline even without a reporter.
	 */
	getConnectionEventReporter?: () => ((event: WsConnectionEvent) => void) | undefined;
};

const defaultClientState = createClientState();
let configuredOptions: WsClientOptions | undefined;
let recoveryCount = 0;
let detachRecovery: (() => void) | undefined;

/** Call before the first subscribe. Ignored (with a warning) once the client exists. */
export const configureWebSocketClient = (options: WsClientOptions): void => {
	if (defaultClientState.client) {
		// eslint-disable-next-line no-console
		console.warn(
			'configureWebSocketClient was called after the subscription client was already created, so these options have been ignored. Configure it while creating the relay environment, before any subscription starts.',
		);
		return;
	}
	configuredOptions = options;
};

const getClientForState = (state: ClientState, url?: string): Client | null => {
	if (isSSR()) {
		return null;
	}

	if (!state.client) {
		const onConnectionEvent = configuredOptions?.getConnectionEventReporter?.();
		const terminate = () => state.client?.terminate();
		const { listeners, isConnected } = createSocketListeners({
			onConnectionEvent,
			pongWaitTimeout: fg('platform_relay_subscription_reliability') ? WS_CLIENT_PONG_WAIT : 0,
			terminate,
		});
		state.isSocketConnected = isConnected;
		const rateLimitedRetryWait =
			fg('platform_relay_subscription_reliability') &&
			fg('platform_relay_subscription_rate_limit_backoff')
				? createRateLimitedRetryWait(WS_CLIENT_MAX_RETRY_WAIT)
				: undefined;
		const retryWait = fg('platform_relay_subscription_reliability')
			? (rateLimitedRetryWait ?? createRetryWait(WS_CLIENT_MAX_RETRY_WAIT))
			: undefined;
		state.abortPendingRetryWait = retryWait?.abort ?? (() => false);
		state.observesSocketState =
			fg('platform_relay_subscription_reliability-additional') &&
			Boolean(onConnectionEvent || fg('platform_relay_subscription_reliability'));
		const options = {
			url: url ?? getAggSubscriptionEndpoint(),
			lazy: true,
			keepAlive: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_LIVENESS_KEEP_ALIVE
				: WS_CLIENT_KEEP_ALIVE,
			retryAttempts: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_CAPPED_RETRIES
				: WS_CLIENT_RETRIES,
			retryWait: retryWait?.wait,
			shouldRetry: fg('platform_relay_subscription_reliability')
				? shouldRetryTransportFailure
				: undefined,
			// 0 = wait forever (graphql-ws default).
			connectionAckWaitTimeout: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_CONNECTION_ACK_WAIT
				: 0,
			lazyCloseTimeout: fg('platform_relay_subscription_connection_reuse')
				? WS_CLIENT_LAZY_CLOSE
				: 0,
			on:
				onConnectionEvent || fg('platform_relay_subscription_reliability') ? listeners : undefined,
			connectionParams: {
				'atl-client-name': 'jira-frontend',
				'atl-client-version': `${(window as any)?.BUILD_KEY ?? ''}::${(window as any)?.SSR_BUILD_KEY ?? ''}`,
				'X-ExperimentalApi': getRelayExperimentalApis().join(','),
			},
		};
		state.client = createClient(options);
		state.lazyCloseTimeout = options.lazyCloseTimeout;
		if (rateLimitedRetryWait) {
			state.client.on('closed', rateLimitedRetryWait.recordClose);
		}
	}
	return state.client;
};

export const getClient = (): Client | null => getClientForState(defaultClientState);

/**
 * Aborts an in-flight backoff, or calls terminate(), on online/visible when disconnected;
 * always on bfcache/resume. Call once at app mount. Reference-counted; detach is idempotent.
 */
export const attachConnectionRecovery = (): (() => void) => {
	if (!fg('platform_relay_subscription_reliability')) {
		return () => {};
	}

	recoveryCount += 1;
	if (recoveryCount === 1) {
		setEndpointRecoveryEnabled(true);
	}
	detachRecovery ??= addRecoveryListeners({
		terminate: () => defaultClientState.client?.terminate(),
		isConnected: () => defaultClientState.isSocketConnected(),
		abortRetryWait: () => defaultClientState.abortPendingRetryWait(),
	});

	let released = false;
	return () => {
		if (released) {
			return;
		}
		released = true;
		recoveryCount -= 1;
		if (recoveryCount === 0) {
			setEndpointRecoveryEnabled(false);
			detachRecovery?.();
			detachRecovery = undefined;
		}
	};
};

export const cleanUpClient = (): void => {
	defaultClientState.client = null;
	configuredOptions = undefined;
	defaultClientState.isSocketConnected = () => false;
	defaultClientState.abortPendingRetryWait = () => false;
	defaultClientState.observesSocketState = false;
	defaultClientState.lazyCloseTimeout = 0;
};

/** Unknown when the product has not enabled socket lifecycle reporting or reliability. */
const getConnectionState = (state: ClientState): 'connected' | 'disconnected' | 'unknown' =>
	fg('platform_relay_subscription_reliability-additional') && state.observesSocketState
		? state.isSocketConnected()
			? 'connected'
			: 'disconnected'
		: 'unknown';

export const getSocketConnectionState = (): 'connected' | 'disconnected' | 'unknown' =>
	getConnectionState(defaultClientState);

/** Resolve a URL once after the SSR check, using the default client's transport policies. */
export const acquireSubscriptionClient = (url: string | (() => string)): ClientLease | null =>
	isSSR()
		? null
		: acquireClient(typeof url === 'function' ? url() : url, getClientForState, getConnectionState);
